import React, { useState, useEffect, useRef } from 'react';

const PartnersSection = () => {
  const [partnerItems, setPartnerItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [activeIndex, setActiveIndex] = useState(0);
  const [isUserInteracting, setIsUserInteracting] = useState(false);

  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [startY, setStartY] = useState(0);
  const [scrollTopState, setScrollTopState] = useState(0);

  const handleImageError = (e) => {
    e.target.src = '/images/placeholder-partner.jpg';
  };

  useEffect(() => {
    const initAOS = async () => {
      try {
        const AOS = (await import('https://cdn.jsdelivr.net/npm/aos@2.3.4/dist/aos.js')).default;
        if (AOS && typeof AOS.init === 'function') {
          AOS.init({ 
            duration: 800,
            once: true,
            easing: 'ease-out-cubic'
          });
        }
      } catch (error) {
        console.log('AOS loading skipped');
      }
    };
    initAOS();
    
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/aos@2.3.4/dist/aos.css';
    document.head.appendChild(link);

    const fetchPartners = async () => {
      try {
        const response = await fetch('https://tbido-website-api.azurewebsites.net/mediaPartnerLogo');
        if (!response.ok) throw new Error('Failed to fetch partner media');
        
        const data = await response.json();
        setPartnerItems(data.media);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching partner media:', error);
        setError('Failed to load partner logos');
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  useEffect(() => {
    if (partnerItems.length === 0 || isUserInteracting || isDragging) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % partnerItems.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [partnerItems.length, isUserInteracting, isDragging]);

  useEffect(() => {
    if (partnerItems.length === 0) return;

    let animationId;
    const step = () => {
      if (scrollRef.current && contentRef.current && !isUserInteracting && !isDragging) {
        scrollRef.current.scrollTop += 0.6;
        
        if (scrollRef.current.scrollTop >= contentRef.current.scrollHeight / 2) {
          scrollRef.current.scrollTop = 0;
        }
      }
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationId);
  }, [partnerItems, isUserInteracting, isDragging]);

  const handleMouseDown = (e) => {
    setIsUserInteracting(true);
    setIsDragging(true);
    setHasDragged(false);
    setStartY(e.pageY - scrollRef.current.offsetTop);
    setScrollTopState(scrollRef.current.scrollTop);
  };

  const handleMouseLeave = () => {
    setIsUserInteracting(false);
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsUserInteracting(false);
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const y = e.pageY - scrollRef.current.offsetTop;
    const walk = (y - startY) * 2;
    
    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
    scrollRef.current.scrollTop = scrollTopState - walk;
  };

  const handleTouchStart = () => setIsUserInteracting(true);
  const handleTouchEnd = () => setIsUserInteracting(false);

  let baseItems = [];
  if (partnerItems.length > 0) {
    let loopCounter = 0;
    while (baseItems.length % 6 !== 0 || baseItems.length < 12) {
      partnerItems.forEach((p, index) => {
        baseItems.push({
          ...p,
          origIndex: index,
          uniqueKey: loopCounter++
        });
      });
    }
  }

  const highlightedPartner = partnerItems[activeIndex];

  return (
    <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden">
      <div className="w-full max-w-6xl 2xl:max-w-5xl mx-auto px-4 sm:px-6 md:px-8">
        
        {/* Title */}
        <div className="text-center mb-8 sm:mb-12" data-aos="fade-up">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-gray-900 mb-2">
            Our{' '}
            <span
              className="font-bold text-transparent bg-clip-text"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
              }}
            >
              Partners
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-2 max-w-2xl mx-auto">
            Collaborating with leading organizations to foster innovation and entrepreneurial growth.
          </p>
        </div>

        <style>
          {`
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            @keyframes fadeIn {
              from { opacity: 0; transform: scale(0.95); }
              to { opacity: 1; transform: scale(1); }
            }
            .animate-partner-fade { animation: fadeIn 0.4s ease-out forwards; }
          `}
        </style>

        {loading ? (
          <div className="text-center py-12"><p className="text-gray-500">Loading partners...</p></div>
        ) : error ? (
          <div className="text-center py-12"><p className="text-red-500">{error}</p></div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-stretch">
            
            <div className="w-full lg:w-[40%] h-56 sm:h-64 lg:h-[400px]" data-aos="fade-right">
              <div className="flex flex-col items-center justify-center bg-gray-50 rounded-2xl p-6 border border-gray-100 shadow-sm h-full w-full relative overflow-hidden group">
                {highlightedPartner && (
                  <div key={activeIndex} className="flex flex-col items-center justify-center w-full h-full animate-partner-fade">
                    <div className="w-full h-24 sm:h-32 flex items-center justify-center mb-4">
                      <img
                        src={highlightedPartner.mediaLink}
                        alt={highlightedPartner.mediaDescription || 'Partner'}
                        onError={handleImageError}
                        className="max-w-[80%] max-h-full object-contain transition-transform duration-700 group-hover:scale-105 drop-shadow-sm"
                      />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 text-center line-clamp-2 px-2">
                      {highlightedPartner.mediaDescription || `Partner ${activeIndex + 1}`}
                    </h3>
                  </div>
                )}
              </div>
            </div>

            <div className="w-full lg:w-[60%] relative h-[320px] sm:h-[380px] lg:h-[400px]" data-aos="fade-left">
              <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-white via-white/40 to-transparent z-10 pointer-events-none"></div>
              
              <div 
                ref={scrollRef}
                onMouseEnter={() => setIsUserInteracting(true)}
                onMouseLeave={handleMouseLeave}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                className="h-full overflow-y-auto hide-scrollbar cursor-grab active:cursor-grabbing px-1"
              >
                <div ref={contentRef} className="flex flex-col">
                  {[1, 2].map((blockIdx) => (
                    <div key={`block-${blockIdx}`} className="grid grid-cols-2 sm:grid-cols-3 gap-4 pb-4">
                      {baseItems.map((partner) => {
                        const isActive = partner.origIndex === activeIndex;
                        return (
                          <div
                            key={`b${blockIdx}-${partner.uniqueKey}`}
                            onClick={() => {
                              if (!hasDragged) {
                                setActiveIndex(partner.origIndex);
                                setIsUserInteracting(true);
                                setTimeout(() => setIsUserInteracting(false), 5000);
                              }
                            }}
                            className={`group flex items-center justify-center bg-white rounded-xl p-4 h-24 sm:h-28 transition-all duration-300 border ${
                              isActive 
                                ? 'border-transparent ring-2 ring-[#0E386B] shadow-md' 
                                : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
                            }`}
                          >
                            <img
                              src={partner.mediaLink}
                              alt="Partner"
                              onError={handleImageError}
                              draggable="false"
                              className={`w-full h-full object-contain transition-transform duration-300 ${
                                isActive ? 'scale-110' : 'group-hover:scale-105'
                              }`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white via-white/40 to-transparent z-10 pointer-events-none"></div>
            </div>

          </div>
        )}
      </div>
    </section>
  );
};

export default PartnersSection;